// Support tickets: workspace-scoped, opt-in like CRM/Finance. Discussion happens
// through the same generic comments used by tasks — no separate messaging system.
import { z } from 'zod';

const Uuid = z.string().uuid();

export const TICKET_PRIORITIES = [0, 1, 2, 3] as const;
export type TicketPriority = (typeof TICKET_PRIORITIES)[number];
export const TICKET_PRIORITY_LABELS: Record<TicketPriority, string> = { 0: 'Bassa', 1: 'Normale', 2: 'Alta', 3: 'Urgente' };

export const TICKET_STATE_CATEGORIES = ['backlog', 'in_progress', 'done', 'canceled'] as const;
/** Seeded on enable: a simple, generic support pipeline. */
export const DEFAULT_TICKET_STATES = [
  { name: 'Aperto', category: 'backlog' },
  { name: 'In lavorazione', category: 'in_progress' },
  { name: 'In attesa del cliente', category: 'in_progress' },
  { name: 'Risolto', category: 'done' },
  { name: 'Chiuso', category: 'canceled' },
] as const;

export const CreateTicketInput = z.object({
  subject: z.string().trim().min(1, 'Scrivi un oggetto').max(200),
  description: z.string().max(20000).default(''),
  priority: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]).default(1),
  assigneeId: Uuid.nullable().optional(),
});
export type CreateTicketInput = z.input<typeof CreateTicketInput>;

export const UpdateTicketInput = z.object({
  subject: z.string().trim().min(1).max(200),
  description: z.string().max(20000),
  priority: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]),
  assigneeId: Uuid.nullable(),
  stateId: Uuid,
}).partial();
export type UpdateTicketInput = z.input<typeof UpdateTicketInput>;

export const TicketListQuery = z.object({
  status: z.enum(['open', 'closed', 'all']).default('open'),
  assigneeId: Uuid.optional(),
});
export type TicketListQuery = z.input<typeof TicketListQuery>;

export interface TicketState {
  id: string;
  name: string;
  category: (typeof TICKET_STATE_CATEGORIES)[number];
  position: number;
  ticketCount: number;
}

export interface TicketConfig {
  workspaceId: string;
  enabled: boolean;
  states: TicketState[];
  permissions: { view: boolean; create: boolean; edit: boolean; delete: boolean; admin: boolean };
}

export interface Ticket {
  id: string;
  key: string;
  workspaceId: string;
  workspaceName: string;
  workspaceColor: string;
  subject: string;
  description: string;
  priority: TicketPriority;
  state: { id: string; name: string; category: TicketState['category'] };
  requestedBy: { id: string; displayName: string };
  assignee: { id: string; displayName: string } | null;
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
  commentCount: number;
  permissions: { edit: boolean; delete: boolean; comment: boolean };
}
